import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  legacyAdminOptionSchema,
  legacyTariffOptionSchema,
  type LegacyAdminOption,
  type LegacyClientList,
  type LegacyTariffOption,
} from '../model/schema';
import type { ClientsListFilter } from '../lib/filters';
import { toLegacyClientList } from '../lib/mappers';

/**
 * Legacy clients admin API — the EXACT paths cpanel used
 * (`features/clients/api/clientsVendors.endpoints.ts`), re-based onto the
 * shared `http` client with the same host mapping `entities/vendor-info`
 * already established for these endpoints: cpanel's `requestMain` paths
 * (`/api/v1/admin/…`) ride the Core host (`VITE_CORE_API_URL` + `/api/v1`),
 * so the absolute URLs are byte-identical. Query objects drop
 * `undefined`/empty values (the old `qs` behavior); responses are parsed
 * tolerantly at the boundary.
 */

const coreBase = `${env.VITE_CORE_API_URL}/api/v1`;
const coreOptions = { baseURL: coreBase };

export type LegacyId = string | number;

function toQuery(filter: object): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '') continue;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      query[key] = String(value);
    }
  }
  return query;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function unwrapPage<T>(
  raw: unknown,
  parse: (item: unknown) => T | undefined,
): { data: T[]; count: number } {
  const list: unknown[] =
    isRecord(raw) && Array.isArray(raw.data)
      ? (raw.data as unknown[])
      : Array.isArray(raw)
        ? (raw as unknown[])
        : [];
  return {
    data: list.map(parse).filter((item): item is T => item !== undefined),
    count: isRecord(raw) && typeof raw.count === 'number' ? raw.count : list.length,
  };
}

export const clientApi = {
  /** `GET admin/vendors/with-orders` — vendor segments (all/business/plain). */
  async vendorsWithOrders(filter: ClientsListFilter): Promise<LegacyClientList> {
    const raw = await http.get<unknown>('admin/vendors/with-orders', {
      ...coreOptions,
      query: toQuery(filter),
    });
    return toLegacyClientList(raw);
  },

  /** `GET admin/clients` — recipients segment. */
  async clients(filter: ClientsListFilter): Promise<LegacyClientList> {
    const raw = await http.get<unknown>('admin/clients', {
      ...coreOptions,
      query: toQuery(filter),
    });
    return toLegacyClientList(raw);
  },

  /** `GET admin/vendor-employees` — staff segment. */
  async employees(filter: ClientsListFilter): Promise<LegacyClientList> {
    const raw = await http.get<unknown>('admin/vendor-employees', {
      ...coreOptions,
      query: toQuery(filter),
    });
    return toLegacyClientList(raw);
  },

  async createClient(data: Record<string, unknown>): Promise<unknown> {
    return await http.post<unknown>('admin/clients', data, coreOptions);
  },

  async createVendor(data: Record<string, unknown>): Promise<unknown> {
    return await http.post<unknown>('admin/vendors', data, coreOptions);
  },

  async updateClient(id: LegacyId, data: Record<string, unknown>): Promise<unknown> {
    return await http.patch<unknown>(`admin/clients/${id}`, data, coreOptions);
  },

  async updateVendor(id: LegacyId, data: Record<string, unknown>): Promise<unknown> {
    return await http.patch<unknown>(`admin/vendors/${id}`, data, coreOptions);
  },

  async deleteClient(id: LegacyId): Promise<unknown> {
    return await http.delete<unknown>(`admin/clients/${id}`, coreOptions);
  },

  async deleteVendor(id: LegacyId): Promise<unknown> {
    return await http.delete<unknown>(`admin/vendors/${id}`, coreOptions);
  },

  /** `PATCH admin/vendors/:id/sanction` — sanction toggle. */
  async sanction(id: LegacyId, isSanctioned: boolean): Promise<unknown> {
    return await http.patch<unknown>(
      `admin/vendors/${id}/sanction`,
      { is_sanctioned: isSanctioned },
      coreOptions,
    );
  },

  /** `PATCH admin/vendors/:id/order-blocked` — order-creation block toggle. */
  async orderBlocked(id: LegacyId, isOrderBlocked: boolean): Promise<unknown> {
    return await http.patch<unknown>(
      `admin/vendors/${id}/order-blocked`,
      { is_order_blocked: isOrderBlocked },
      coreOptions,
    );
  },

  /** `PATCH admin/vendors/:id/sales-manager` — `0` unassigns the manager. */
  async salesManager(vendorId: LegacyId, salesManagerId: LegacyId): Promise<unknown> {
    return await http.patch<unknown>(
      `admin/vendors/${vendorId}/sales-manager`,
      { sales_manager_id: salesManagerId },
      coreOptions,
    );
  },

  /** `GET admin/vendors/export` (blob) — the header download action. */
  async exportVendors(params: Record<string, string>): Promise<Blob> {
    return await http.getBlob('admin/vendors/export', {
      ...coreOptions,
      query: params,
    });
  },

  /** `GET admin/tariffs` — parent tariffs for the filter select. */
  async tariffs(
    filter: { limit?: number; is_parents?: boolean } = {},
  ): Promise<{ data: LegacyTariffOption[]; count: number }> {
    const raw = await http.get<unknown>('admin/tariffs', {
      ...coreOptions,
      query: toQuery({ limit: 50, is_parents: true, ...filter }),
    });
    return unwrapPage(raw, (item) => legacyTariffOptionSchema.safeParse(item).data);
  },

  /** `GET admin/users` — B2B admins for the sales-manager picker. */
  async admins(
    filter: { limit?: number; roles?: string } = {},
  ): Promise<{ data: LegacyAdminOption[]; count: number }> {
    const raw = await http.get<unknown>('admin/users', {
      ...coreOptions,
      query: toQuery({ limit: 50, ...filter }),
    });
    return unwrapPage(raw, (item) => legacyAdminOptionSchema.safeParse(item).data);
  },
};
